import { format } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import type { PresenceStatus, UserConnection, UserMessage, UserPresenceMeta } from '@/hooks/useUserMessages';
import { cn } from '@/lib/utils';
import { getMessengerActionPreview } from './actionPayload';

const presenceDotClassName: Record<PresenceStatus, string> = {
  online: 'bg-emerald-500',
  away: 'bg-amber-400',
  dnd: 'bg-red-500',
  offline: 'bg-slate-400',
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return format(date, sameDay ? 'p' : 'MMM d');
};

const getConversationSnippet = (content?: string) => {
  if (!content) {
    return 'No messages yet';
  }

  const actionPreview = getMessengerActionPreview(content);
  if (actionPreview) {
    return actionPreview;
  }

  return content
    .replace(/!\[[^\]]*\]\(([^)]+)\)/g, '[Image]')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/https?:\/\/\S+/g, 'Link')
    .replace(/\s+/g, ' ')
    .trim();
};

interface ContactsListProps {
  currentUserId: string;
  connections: UserConnection[];
  messages: UserMessage[];
  presenceMeta: Record<string, UserPresenceMeta>;
  selectedUserId: string | null;
  onSelect: (userId: string) => void;
}

export default function ContactsList({
  currentUserId,
  connections,
  messages,
  presenceMeta,
  selectedUserId,
  onSelect,
}: ContactsListProps) {
  const acceptedConnections = connections.filter((connection) => connection.status === 'accepted');

  const contactItems = acceptedConnections
    .map((connection) => {
      const otherUserId = connection.user_id === currentUserId ? connection.connected_user_id : connection.user_id;
      const relevantMessages = messages
        .filter(
          (message) =>
            (message.sender_id === currentUserId && message.recipient_id === otherUserId) ||
            (message.sender_id === otherUserId && message.recipient_id === currentUserId)
        )
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const lastMessage = relevantMessages[relevantMessages.length - 1];
      const unreadCount = relevantMessages.filter(
        (message) => message.sender_id === otherUserId && message.recipient_id === currentUserId && !message.read
      ).length;
      const presence = presenceMeta[otherUserId];
      const status = presence?.status ?? 'offline';
      const statusMessage = presence?.status_message ?? '';
      const sortWeight = status === 'online' ? 0 : status === 'away' ? 1 : status === 'dnd' ? 2 : 3;

      return {
        id: otherUserId,
        avatar: connection.connected_user_avatar,
        name: connection.connected_user_name || 'User',
        lastMessageSnippet: getConversationSnippet(lastMessage?.content),
        lastMessageTimestamp: lastMessage?.created_at || connection.updated_at,
        unreadCount,
        status,
        statusMessage,
        hasStatusMessage: Boolean(statusMessage.trim()),
        sortWeight,
      };
    })
    .sort((a, b) => {
      if (a.sortWeight !== b.sortWeight) {
        return a.sortWeight - b.sortWeight;
      }

      return new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime();
    });

  if (contactItems.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-border/80 bg-muted/20 px-6 text-center">
        <p className="text-sm font-medium text-foreground">No accepted connections yet</p>
        <p className="mt-1 text-xs text-muted-foreground">Accepted connections will appear here as Messenger conversations.</p>
      </div>
    );
  }

  return (
    <div role="tablist" aria-label="Conversations" className="space-y-2">
      {contactItems.map((contact) => (
        <button
          key={contact.id}
          type="button"
          role="tab"
          aria-selected={selectedUserId === contact.id}
          onClick={() => onSelect(contact.id)}
          className={cn(
            'flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
            selectedUserId === contact.id
              ? 'border-primary bg-accent/60 shadow-sm'
              : 'border-border/70 bg-card/70 hover:border-primary/30 hover:bg-accent/30'
          )}
        >
          <div className="relative shrink-0">
            <Avatar className="h-11 w-11 border border-border/50">
              <AvatarImage src={contact.avatar} alt={contact.name} />
              <AvatarFallback>{contact.name.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span
              className={cn(
                'absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-card',
                presenceDotClassName[contact.status]
              )}
              aria-hidden="true"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">{contact.name}</p>
                {contact.hasStatusMessage && (
                  <p className="truncate text-xs text-muted-foreground">{contact.statusMessage}</p>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className="text-[11px] text-muted-foreground">{formatTimestamp(contact.lastMessageTimestamp)}</span>
                {contact.unreadCount > 0 && (
                  <Badge className="h-5 min-w-5 rounded-full px-1.5 text-[11px] leading-none">
                    {contact.unreadCount > 99 ? '99+' : contact.unreadCount}
                  </Badge>
                )}
              </div>
            </div>

            <p className={cn('text-xs text-muted-foreground', contact.hasStatusMessage ? 'mt-2 line-clamp-2' : 'mt-1 truncate')}>
              {contact.lastMessageSnippet}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}